import { create } from 'zustand'
import { styleSessionAPI, StyleSession, gitAPI } from '../services/api'

interface StyleSessionBranch {
  name: string
  isCurrent: boolean
}

interface StyleSessionStore {
  // State
  styleSessions: StyleSession[]
  activeStyleSessionId: string | null
  isLoading: boolean
  error: string | null

  // Git branching state
  branches: Record<string, StyleSessionBranch[]>
  currentBranch: Record<string, string | null>
  isGitRepo: Record<string, boolean>
  isBranchLoading: boolean
  branchError: string | null

  // Actions
  setActiveStyleSession: (sessionId: string | null) => void
  addStyleSession: (session: StyleSession) => void
  loadStyleSessions: () => Promise<void>
  deleteStyleSession: (sessionId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Git branching actions
  loadBranches: (sessionId: string) => Promise<void>
  switchBranch: (sessionId: string, branchName: string) => Promise<void>
  setBranchLoading: (loading: boolean) => void
  setBranchError: (error: string | null) => void
}

export const useStyleSessionStore = create<StyleSessionStore>((set) => ({
  // Initial state
  styleSessions: [],
  activeStyleSessionId: null,
  isLoading: false,
  error: null,

  // Git branching initial state
  branches: {},
  currentBranch: {},
  isGitRepo: {},
  isBranchLoading: false,
  branchError: null,

  // Actions
  setActiveStyleSession: (sessionId) => {
    set({ activeStyleSessionId: sessionId })
  },

  addStyleSession: (session) => {
    set((state) => ({
      styleSessions: [session, ...state.styleSessions]
    }))
  },

  loadStyleSessions: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await styleSessionAPI.list()
      if (response.success) {
        set({ styleSessions: response.data })
      } else {
        set({ error: 'Failed to load style sessions' })
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load style sessions' })
    } finally {
      set({ isLoading: false })
    }
  },

  deleteStyleSession: async (sessionId) => {
    try {
      set({ isLoading: true, error: null })
      const response = await styleSessionAPI.delete(sessionId)
      if (response.success) {
        set((state) => ({
          styleSessions: state.styleSessions.filter(session => session.sessionId !== sessionId),
          activeStyleSessionId: state.activeStyleSessionId === sessionId ? null : state.activeStyleSessionId
        }))
      } else {
        set({ error: 'Failed to delete style session' })
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete style session' })
    } finally {
      set({ isLoading: false })
    }
  },

  setLoading: (loading) => {
    set({ isLoading: loading })
  },

  setError: (error) => {
    set({ error })
  },

  // Git branching actions
  loadBranches: async (sessionId) => {
    try {
      set({ isBranchLoading: true, branchError: null })
      const response = await gitAPI.getBranches(sessionId)
      
      set((state) => ({
        branches: {
          ...state.branches,
          [sessionId]: response.branches
        },
        currentBranch: {
          ...state.currentBranch,
          [sessionId]: response.currentBranch
        },
        isGitRepo: {
          ...state.isGitRepo,
          [sessionId]: true // Git is initialized for all sessions
        }
      }))
    } catch (error) {
      set({ branchError: error instanceof Error ? error.message : 'Failed to load branches' })
    } finally {
      set({ isBranchLoading: false })
    }
  },

  switchBranch: async (sessionId, branchName) => {
    try {
      set({ isBranchLoading: true, branchError: null })
      const response = await gitAPI.switchBranch(sessionId, branchName)
      
      if (response.success) {
        // Reload branches to get updated state
        const branchesResponse = await gitAPI.getBranches(sessionId)
        
        set((state) => ({
          branches: {
            ...state.branches,
            [sessionId]: branchesResponse.branches
          },
          currentBranch: {
            ...state.currentBranch,
            [sessionId]: branchesResponse.currentBranch
          }
        }))
      } else {
        set({ branchError: 'Failed to switch branch' })
      }
    } catch (error) {
      set({ branchError: error instanceof Error ? error.message : 'Failed to switch branch' })
    } finally {
      set({ isBranchLoading: false })
    }
  },

  setBranchLoading: (loading) => {
    set({ isBranchLoading: loading })
  },

  setBranchError: (error) => {
    set({ branchError: error })
  }
}))